import { useMemo, useState, useCallback, useRef } from "react";
import ReportsPagesContent from "src/component/ReportsPagesContent";
import SearchWrapper from "src/component/searchSection";
import { GetWaitingForPacking, SetRequestAsPacked } from "src/requests/waitingPackingRequests";
import { ColumnType, CellOriginal } from "src/types/Table/table";
import { WaitingForPackingRequest } from "src/types/Requests/waitingPackingRequests";
import CheckBox from "src/component/CheckBox";
import { useMutation } from "src/hooks/useMutation";
import SelectedActionsBar from "./SelectedActionsBar";
import Status from "src/component/Status/Status";
import { BOOLEAN_COLOR_MAP } from "src/common/constants";
import BaseBtn from "src/component/ActionBtn/BaseBtn";
import { usePrintImages } from "src/hooks/usePrintImages";
import PrintRequest from "../../component/PrintedPDF/PrintRequestTable";
import { SummaryPopup } from "src/component/popup/SummaryPopup/SummaryPopup";

const WaitingForPacking = () => {
    const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
    const [selectedPrintRequestId, setSelectedPrintRequestId] = useState<number | null>(null)
    const [pageData, setPageData] = useState<WaitingForPackingRequest[]>([])

    const printRequestRef = useRef<HTMLDivElement>(null);
    const printRequestIsNeedCoolingMedicines = useRef<HTMLDivElement>(null);

    const { mutate, isPending } = useMutation();


    const { handlePrint } = usePrintImages({ title: "request-print" })

    const handleSelectItem = useCallback((requestId: number, isSelected: boolean) => {
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(requestId);
            } else {
                newSet.delete(requestId);
            }
            return newSet;
        });
    }, []);

    const handleToggleSelectAll = (items: WaitingForPackingRequest[]) => {
        setSelectedItems((prev) => {
            const newSet = new Set(prev);
            const allSelected = items.every(item => newSet.has(item.requestId));
            allSelected ? items.forEach(item => newSet.delete(item.requestId)) : items.forEach(item => newSet.add(item.requestId))
            return newSet
        })
    }

    const handleClearSelection = useCallback(() => {
        setSelectedItems(new Set());
    }, []);

    const isAllSelected = useCallback(
        (items: WaitingForPackingRequest[]) => {
            return (
                items.length > 0 &&
                items.every((item) => selectedItems.has(item.requestId))
            );
        },
        [selectedItems]
    );

    const onSubmit = useCallback(() => {
        const selectedIds = Array.from(selectedItems);
        if (selectedIds.length === 0) return;
        mutate({
            apiMethod: SetRequestAsPacked,
            body: selectedIds,
            actionType: "post",
            name: "requestsAsPacked",
            onSuccess: () => {
                handleClearSelection();
                confirmationModalRef.current.click()
            },
        });
    }, [selectedItems, mutate, handleClearSelection]);

    const handlePrintRequest = (id: number) => {
        setSelectedPrintRequestId(id)
    }

    const confirmationModalRef = useRef<any>(null);

    const columns: ColumnType<WaitingForPackingRequest & { keyword?: string, waitingForPackingDate?: string }>[] = useMemo(() => [
        {
            Header: "selectRequestIds",
            Cell: ({ row: { original } }: CellOriginal<WaitingForPackingRequest>) => {
                const isSelected = selectedItems.has(original.requestId);
                return (
                    <CheckBox
                        value={isSelected}
                        setValue={(value: boolean) => handleSelectItem(original.requestId, value)}
                    />
                );
            },
        },
        {
            Header: "keyword",
            accessor: "keyword",
            canSearch: true,
            visible: false
        },

        {
            Header: "requestId",
            accessor: "requestId",
        },
        {
            Header: "patientName",
            accessor: "patientName"
        },
        {
            Header: "patientNationalId",
            accessor: "patientNationalId"
        },
        {
            Header: "decisionNumber",
            accessor: "decisionNumber"
        },
        {
            Header: "serviceType",
            accessor: "serviceType"
        },
        {
            Header: "isNeedCooling",
            Cell: ({ row: { original } }: CellOriginal<WaitingForPackingRequest>) => {
                return <Status cellContent={original?.isNeedCooling.toString()} cellColorMap={BOOLEAN_COLOR_MAP} />
            }
        },
        {
            Header: "totalCost",
            accessor: "totalCost"
        },
        {
            Header: "totalPatientCostShare",
            accessor: "totalPatientCostShare"
        },
        {
            Header: "requestPeriod",
            accessor: "requestPeriod"
        },
        {
            Header: "waitingForPackingDate",
            accessor: "waitingForPackingDate",
            canSearch: true,
            type: "Date",
            isMulti: true,
            probName: "packedDate",
        },
        {
            Header: "waitingForPackingBy",
            accessor: "waitingForPackingBy"
        },
        {
            Header: "requestDueDate",
            accessor: "requestDueDate"
        },
        {
            Header: "actions",
            Cell: ({ row: { original } }: CellOriginal<WaitingForPackingRequest>) => {
                return (
                    <BaseBtn
                        icon={"material-symbols:print-rounded"}
                        className='update'
                        ApplyAction={() => handlePrintRequest(original.requestId)}
                    />
                )
            }
        }
    ], [selectedItems, handleSelectItem, handlePrintRequest])

    const convertItemsToArray = useMemo(
        () => Array.from(selectedItems).map(id => ({ value: id })),
        [selectedItems]
    );

    return (
        <div>
            <SummaryPopup
                modalID={confirmationModalRef}
                items={convertItemsToArray}
                onConfirm={() => onSubmit()}
                onCancel={() => confirmationModalRef?.current?.click()}
                isLoading={isPending}
                summaryTableTitle="selected-requests"
            />
            <PrintRequest
                printRefs={[printRequestRef, printRequestIsNeedCoolingMedicines]}
                selectedPrintRequestId={selectedPrintRequestId}
                handlePrint={handlePrint}
                setSelectedPrintRequestId={setSelectedPrintRequestId}

            />
            <ReportsPagesContent columns={columns}
                tableAPI={
                    (searchData: any, tableFiller: any) =>
                        GetWaitingForPacking(searchData, tableFiller, Number(sessionStorage.getItem("hopsitalId")))
                }
                onDataLoad={setPageData}
            >
                <SearchWrapper />

                <SelectedActionsBar
                    selectedCount={selectedItems.size}
                    onClear={handleClearSelection}
                    onProcess={() => confirmationModalRef.current.click()}
                    handleToggleSelectAll={handleToggleSelectAll}
                    isAllSelected={isAllSelected}
                    items={pageData}
                />

            </ReportsPagesContent>
        </div>
    )
}

export default WaitingForPacking;